import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { usePortalAuth } from './PortalAuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { usePortalDestination } from './PortalDestinationContext';
import { PortalIdentitySheet } from './PortalIdentitySheet';
import { PortalMobileNavigation } from './PortalMobileNavigation';
import { PortalSideRail } from './PortalSideRail';
import { PortalUnreadProvider } from './PortalUnreadContext';
import { PortalOfflineBanner } from './ui/PortalOfflineBanner';
import { PortalInstallBanner } from './ui/PortalInstallBanner';
import { PortalThemeToggle } from './PortalThemeToggle';
import { PortalPersonSwitcher } from './PortalPersonSwitcher';
import { PortalGrowthNudge } from './PortalGrowthNudge';
import { DemoBanner } from '../components/DemoBanner';
import { LOGO_MARK } from '../lib/brand';
import './portalMobile.css';
import './portalFoundation.css';

export default function PortalLayout({ children }) {
  return <PortalUnreadProvider><PortalShell>{children}</PortalShell></PortalUnreadProvider>;
}

function PortalShell({ children }) {
  const { patient, activeMember, setActiveMember } = usePortalAuth();
  const { href, pharmacy, parsed, accepting_requests: accepting, ineligible_reason: ineligibleReason } = usePortalDestination();
  const scrollMap = useRef({});
  useEffect(() => {
    const key = parsed.page;
    window.scrollTo(0, scrollMap.current[key] || 0);
    const onScroll = () => { scrollMap.current[key] = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
    // Restoring per-tab scroll position only needs to react to the active tab, not query/search changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsed.page]);
  return <div className="portal-shell" data-testid="portal-app-shell">
    <DemoBanner variant="portal" show={!!patient?.is_demo_account} />
    <header className="portal-header">
      <div className="portal-topbar max-w-6xl mx-auto flex items-center justify-between gap-3 px-4 py-3">
        <PortalIdentitySheet />
        <div className="flex items-center gap-2 shrink-0" data-testid="portal-header-actions">
          <PortalPersonSwitcher />
          <PortalThemeToggle />
          <span className="portal-dutilyne-mark" title="Powered by DutiLyne" data-testid="portal-dutilyne-mark">
            <img src={LOGO_MARK} alt="" aria-hidden="true" />
          </span>
          <Link to={href('profile')} className="portal-profile-link" aria-label="My account" data-testid="portal-profile-link">
            <Avatar className="h-9 w-9"><AvatarImage src={patient?.avatar_url || ''} alt="" />
              <AvatarFallback className="portal-avatar-fallback">{(patient?.name || 'P')[0].toUpperCase()}</AvatarFallback>
            </Avatar>
          </Link>
        </div>
      </div>
    </header>
    <PortalOfflineBanner />
    <PortalInstallBanner />
    <PortalGrowthNudge />
    {activeMember && (
      <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-sm px-4 py-2.5 flex flex-wrap items-center justify-center gap-2"
        data-testid="portal-acting-as-banner">
        <span>You are managing <strong>{activeMember.name}</strong>&apos;s care.</span>
        <button type="button" className="underline font-medium" onClick={() => setActiveMember('')}
          data-testid="portal-acting-as-exit">Switch back to me</button>
      </div>
    )}
    {pharmacy && accepting === false && (
      <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm px-4 py-2.5 text-center"
        data-testid="portal-not-accepting-banner">
        {ineligibleReason || 'This pharmacy is not currently accepting new requests.'} Your history and messages are safe.
      </div>
    )}
    <div className="portal-shell-body">
      <PortalSideRail />
      <div className="portal-shell-main">
        <main className="portal-main max-w-6xl mx-auto px-4 py-6" data-testid="portal-main">{children}</main>
        <footer className="portal-footer text-center text-xs text-slate-400 px-4 pb-8" data-testid="portal-emergency-notice">DutiLyne © {new Date().getFullYear()} · Not for medical emergencies.</footer>
      </div>
    </div>
    <PortalMobileNavigation />
  </div>;
}
